import React, { useState, useMemo } from 'react';
import styles from './CategoryExplorerView.module.css';
import { useCategoryExplorerTree, getTier1Categories, getTier2Subcategories } from '../../hooks/useCategoryExplorerTree';

interface CategorySelection {
  readerAge?: string;
  literaryType?: string;
  genre?: string;
  selectedCategoryIds?: string[];
}

interface AuthorSelection {
  authorAge?: string;
  experience?: string;
  region?: string;
}

const CategoryExplorerView: React.FC = () => {
  const [literaryWork, setLiteraryWork] = useState<CategorySelection>({});
  const [authorAttributes, setAuthorAttributes] = useState<AuthorSelection>({});

  // Fetch dynamic category tree
  const { data: categoryTree, isLoading, error } = useCategoryExplorerTree();

  // Literary Work cascading options - derived from live data
  const readerAgeOptions = useMemo(() => {
    if (!categoryTree) return [];
    return Object.keys(categoryTree.reader_segments) as Array<'Books for Adults' | 'Books for Children & Young Adult'>;
  }, [categoryTree]);

  const literaryTypeOptions = useMemo(() => {
    if (!categoryTree || !literaryWork.readerAge) return [];
    const segment = literaryWork.readerAge as 'Books for Adults' | 'Books for Children & Young Adult';
    const tier1Categories = getTier1Categories(categoryTree, segment);
    return tier1Categories.map(cat => cat.name);
  }, [categoryTree, literaryWork.readerAge]);

  const genreOptions = useMemo(() => {
    if (!categoryTree || !literaryWork.readerAge || !literaryWork.literaryType) return [];
    const segment = literaryWork.readerAge as 'Books for Adults' | 'Books for Children & Young Adult';
    const tier1Categories = getTier1Categories(categoryTree, segment);
    const selectedTier1 = tier1Categories.find(cat => cat.name === literaryWork.literaryType);
    if (!selectedTier1) return [];
    const tier2Subcategories = getTier2Subcategories(selectedTier1);
    return tier2Subcategories.map(cat => cat.name);
  }, [categoryTree, literaryWork.readerAge, literaryWork.literaryType]);

  // Author attributes - derived from live data
  const authorAttributeCounts = useMemo(() => {
    if (!categoryTree) return null;
    return {
      selfPublished: categoryTree.author_attributes.self_published_count,
      debutOnly: categoryTree.author_attributes.debut_only_count,
      regional: categoryTree.author_attributes.regional_restriction_count,
      youngAuthors: categoryTree.author_attributes.young_authors_count
    };
  }, [categoryTree]);

  // Author selection options (still using predefined buckets for UX clarity)
  const experienceOptions = useMemo(() => [
    { label: 'First Literary Work (Debut)', value: 'debut', count: authorAttributeCounts?.debutOnly || 0 },
    { label: 'Experienced Author', value: 'experienced', count: 0 }
  ], [authorAttributeCounts]);

  const publishingOptions = useMemo(() => [
    { label: 'Self-Published / Indie Press', value: 'self_published', count: authorAttributeCounts?.selfPublished || 0 },
    { label: 'Traditional Publishing', value: 'traditional', count: 0 }
  ], [authorAttributeCounts]);

  const regionOptions = useMemo(() => [
    { label: 'Geographic Restrictions Apply', value: 'regional', count: authorAttributeCounts?.regional || 0 },
    { label: 'Open to All Regions', value: 'global', count: 0 }
  ], [authorAttributeCounts]);

  const handleReaderAgeSelect = (age: string) => {
    setLiteraryWork({ readerAge: age });
  };

  const handleLiteraryTypeSelect = (type: string) => {
    if (!categoryTree || !literaryWork.readerAge) return;

    const segment = literaryWork.readerAge as 'Books for Adults' | 'Books for Children & Young Adult';
    const tier1Categories = getTier1Categories(categoryTree, segment);
    const selectedTier1 = tier1Categories.find(cat => cat.name === type);

    setLiteraryWork(prev => ({
      ...prev,
      literaryType: type,
      genre: undefined,
      selectedCategoryIds: selectedTier1?.category_ids
    }));
  };

  const handleGenreSelect = (genre: string) => {
    if (!categoryTree || !literaryWork.readerAge || !literaryWork.literaryType) return;

    const segment = literaryWork.readerAge as 'Books for Adults' | 'Books for Children & Young Adult';
    const tier1Categories = getTier1Categories(categoryTree, segment);
    const selectedTier1 = tier1Categories.find(cat => cat.name === literaryWork.literaryType);
    if (!selectedTier1) return;

    const tier2Subcategories = getTier2Subcategories(selectedTier1);
    const selectedTier2 = tier2Subcategories.find(cat => cat.name === genre);

    setLiteraryWork(prev => ({
      ...prev,
      genre,
      selectedCategoryIds: selectedTier2?.category_ids
    }));
  };

  const handleExperienceSelect = (value: string) => {
    setAuthorAttributes(prev => ({ ...prev, experience: value }));
  };

  const handlePublishingSelect = (value: string) => {
    setAuthorAttributes(prev => ({ ...prev, authorAge: value }));
  };

  const handleRegionSelect = (value: string) => {
    setAuthorAttributes(prev => ({ ...prev, region: value }));
  };

  const handleReset = () => {
    setLiteraryWork({});
    setAuthorAttributes({});
  };

  const handleSearchPrograms = () => {
    // Navigate to programs search with category IDs
    const categoryIds = literaryWork.selectedCategoryIds || [];
    const params = new URLSearchParams();

    if (categoryIds.length > 0) {
      params.set('category_ids', categoryIds.join(','));
    }

    if (literaryWork.readerAge) params.set('reader_segment', literaryWork.readerAge);
    if (literaryWork.literaryType) params.set('tier1', literaryWork.literaryType);
    if (literaryWork.genre) params.set('tier2', literaryWork.genre);

    window.location.hash = `#data-quality${params.toString() ? '?' + params.toString() : ''}`;
  };

  // Loading and error states
  if (isLoading) {
    return (
      <div className={styles.root}>
        <header className={styles.header}>
          <h1 className={styles.title}>Category Discovery</h1>
          <p className={styles.subtitle}>Loading category data...</p>
        </header>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <header className={styles.header}>
          <h1 className={styles.title}>Category Discovery</h1>
          <p className={styles.subtitle} style={{ color: '#ef4444' }}>
            Error loading categories: {error.message}
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <h1 className={styles.title}>Category Discovery</h1>
        <p className={styles.subtitle}>
          Build your category profile by selecting attributes for your literary work and author background
        </p>
      </header>

      <div className={styles.splitLayout}>
        {/* Left Panel: Literary Work Attributes */}
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Select Attributes of Literary Work</h2>

          {/* Progress breadcrumb */}
          <div className={styles.breadcrumb}>
            {literaryWork.readerAge && <span className={styles.breadcrumbItem}>{literaryWork.readerAge}</span>}
            {literaryWork.literaryType && <span className={styles.breadcrumbItem}>{literaryWork.literaryType}</span>}
            {literaryWork.genre && <span className={styles.breadcrumbItem}>{literaryWork.genre}</span>}
          </div>

          {/* Level 1: Reader Age */}
          <div className={styles.selectorGroup}>
            <h3 className={styles.selectorLabel}>Reader Age Group</h3>
            <div className={styles.tileGrid}>
              {readerAgeOptions.map(age => (
                <button
                  key={age}
                  type="button"
                  className={`${styles.tile} ${literaryWork.readerAge === age ? styles.tileActive : ''}`}
                  onClick={() => handleReaderAgeSelect(age)}
                >
                  {age}
                </button>
              ))}
            </div>
          </div>

          {/* Level 2: Literary Type */}
          {literaryWork.readerAge && literaryTypeOptions.length > 0 && (
            <div className={styles.selectorGroup}>
              <h3 className={styles.selectorLabel}>Literary Type</h3>
              <div className={styles.tileGrid}>
                {literaryTypeOptions.map(type => (
                  <button
                    key={type}
                    type="button"
                    className={`${styles.tile} ${literaryWork.literaryType === type ? styles.tileActive : ''}`}
                    onClick={() => handleLiteraryTypeSelect(type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Level 3: Genre/Subcategory */}
          {literaryWork.literaryType && genreOptions.length > 0 && (
            <div className={styles.selectorGroup}>
              <h3 className={styles.selectorLabel}>Genre / Subcategory</h3>
              <div className={styles.tileGrid}>
                {genreOptions.map(genre => (
                  <button
                    key={genre}
                    type="button"
                    className={`${styles.tile} ${literaryWork.genre === genre ? styles.tileActive : ''}`}
                    onClick={() => handleGenreSelect(genre)}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Category count display */}
          {literaryWork.genre && categoryTree && (
            <div className={styles.selectorGroup}>
              <div style={{
                padding: '0.75rem',
                background: '#d1fae5',
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: '#065f46'
              }}>
                ✓ {literaryWork.selectedCategoryIds?.length || 0} award categories selected
              </div>
            </div>
          )}
        </section>

        {/* Right Panel: Author Related Attributes */}
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Select Author Related Attributes</h2>

          {/* Progress breadcrumb */}
          <div className={styles.breadcrumb}>
            {authorAttributes.experience && <span className={styles.breadcrumbItem}>{experienceOptions.find(e => e.value === authorAttributes.experience)?.label}</span>}
            {authorAttributes.authorAge && <span className={styles.breadcrumbItem}>{publishingOptions.find(p => p.value === authorAttributes.authorAge)?.label}</span>}
            {authorAttributes.region && <span className={styles.breadcrumbItem}>{regionOptions.find(r => r.value === authorAttributes.region)?.label}</span>}
          </div>

          {/* Level 1: Author Experience */}
          <div className={styles.selectorGroup}>
            <h3 className={styles.selectorLabel}>Author Experience</h3>
            <div className={styles.tileGrid}>
              {experienceOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.tile} ${authorAttributes.experience === option.value ? styles.tileActive : ''}`}
                  onClick={() => handleExperienceSelect(option.value)}
                >
                  {option.label}
                  {option.count > 0 && <span className={styles.tileCount}> ({option.count})</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Level 2: Publishing Path */}
          {authorAttributes.experience && (
            <div className={styles.selectorGroup}>
              <h3 className={styles.selectorLabel}>Publishing Path</h3>
              <div className={styles.tileGrid}>
                {publishingOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={`${styles.tile} ${authorAttributes.authorAge === option.value ? styles.tileActive : ''}`}
                    onClick={() => handlePublishingSelect(option.value)}
                  >
                    {option.label}
                    {option.count > 0 && <span className={styles.tileCount}> ({option.count})</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Level 3: Geographic Scope */}
          {authorAttributes.authorAge && (
            <div className={styles.selectorGroup}>
              <h3 className={styles.selectorLabel}>Geographic Scope</h3>
              <div className={styles.tileGrid}>
                {regionOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={`${styles.tile} ${authorAttributes.region === option.value ? styles.tileActive : ''}`}
                    onClick={() => handleRegionSelect(option.value)}
                  >
                    {option.label}
                    {option.count > 0 && <span className={styles.tileCount}> ({option.count})</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Author attribute summary */}
          {categoryTree && (
            <div className={styles.selectorGroup}>
              <div style={{
                padding: '0.75rem',
                background: '#fef3c7',
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: '#92400e'
              }}>
                📊 {authorAttributeCounts?.selfPublished || 0} self-pub • {authorAttributeCounts?.debutOnly || 0} debut-only categories in database
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Action buttons */}
      <footer className={styles.footer}>
        <button type="button" className={styles.resetButton} onClick={handleReset}>
          Reset All
        </button>
        <button
          type="button"
          className={styles.searchButton}
          onClick={handleSearchPrograms}
          disabled={!literaryWork.readerAge && !authorAttributes.authorAge}
        >
          Search Programs
        </button>
      </footer>
    </div>
  );
};

export default CategoryExplorerView;
